import React from 'react';
import I18n from 'i18n-js';

import bind from '../../bind';

import MarketingCalloutCard from './marketing_callout_card';
import OverflowingRow from '../../components/overflowing_row';
import Tiles from '../../components/tiles';
import SanitizedRender from '../../components/sanitized_render';

import { IDynamicComponentProps } from '../dynamic_component_props';
import CoreLink from '../../components/core_link';

import { imageUploadURL } from '../../url_helpers';

const SIDEBAR_SECTION = 'sidebar';
const COMPONENT_NAME = 'MarketingCallout';

interface CalloutData {
  title: string;
  titleHtml?: string;
  subtitle?: string;
  subtitleHtml?: string;
  image: string;
  targetHref: string;
  channel?: string;
  pageType?: string;
}

export interface IProps extends IDynamicComponentProps {
  callouts: CalloutData[];
  displayAsGrid?: boolean;
  title?: string;
  titleHtml?: string;
  subtitleHtml?: string;
  ledeHtml?: string;
  ctaText?: string;
  ctaTargetHref?: string;
  tileSize?: string;
  jumplinkSlug?: string;
  trackingName?: string;
}

export default class MarketingCallout extends React.Component<IProps, null> {
  // needed to prevent gql load order error
  static defaultProps = {
    callouts: [],
  };

  trackingName() {
    return this.props.trackingName ?? COMPONENT_NAME;
  }

  shouldBeTwoWide() {
    return this.props.tileSize === 'twoWide';
  }

  // includes default behavior (for legacy support)
  shouldBeThreeWide() {
    return (
      !this.props.tileSize ||
      this.props.tileSize === 'threeWide'
    );
  }

  shouldBeSquare() {
    return this.props.tileSize === 'squareImages';
  }

  shouldGrow() {
    if (this.shouldBeSquare()) return false;
    return (
      this.shouldBeThreeWide() ||
      this.shouldBeTwoWide() ||
      (this.getLength() === 1)
    );
  }

  // this allows content managers to be able to count on large tiles in this context
  // to be a multiple of 4 (default large tile behavior can result in 5-wide grids)
  shouldBeFourWideMax() {
    if (!this.props.displayAsGrid) return false;
    return this.shouldBeLargeTiles();
  }

  shouldBeLargeTiles() {
    return this.props.tileSize === 'largeTiles';
  }

  shouldBeSmallTiles() {
    return this.props.tileSize === 'smallTiles';
  }

  // we can't center flex items that are expected to overflow
  // and anything singleRow could potentially overflow
  shouldCenter() {
    if (!this.props.displayAsGrid) return false;
    return this.shouldBeThreeWide() || this.shouldBeSquare();
  }

  shouldRenderForSidebar() {
    return this.props.layoutSection === SIDEBAR_SECTION;
  }

  getLength() {
    if (!this.props.callouts) return 0;
    return this.props.callouts.length;
  }

  @bind
  renderCallout(calloutData, idx) {
    return (
      <MarketingCalloutCard
        key={calloutData.title}
        channel={calloutData.channel}
        imageURL={imageUploadURL(calloutData.image)}
        pageType={calloutData.pageType}
        subtitle={calloutData.subtitleHtml || calloutData.subtitle}
        targetHref={calloutData.targetHref}
        title={calloutData.titleHtml || calloutData.title}
        wideCard={this.shouldBeTwoWide() && !this.shouldRenderForSidebar()}
        squareImage={this.shouldBeSquare()}
        position={idx}
        smallTile={this.shouldBeSmallTiles()}
        trackingComponentName={this.trackingName()}
      />
    );
  }

  renderCallouts() {
    return this.props.callouts.map(this.renderCallout);
  }

  renderForSidebar() {
    return (
      <div className="marketing-callout-sidebar-container">
        {this.renderCallouts()}
      </div>
    );
  }

  renderAction() {
    if (!this.props.ctaTargetHref) {
      return null;
    }

    return (
      <CoreLink
        className={this.props.displayAsGrid && 'button button--primary'}
        to={this.props.ctaTargetHref}
      >
        {this.props.ctaText
          ? this.props.ctaText
          : I18n.t('cms.marketingCallout.seeMore')}
      </CoreLink>
    );
  }

  renderTitle() {
    return (
      <SanitizedRender
        html={this.props.titleHtml || this.props.title}
      />
    );
  }

  render() {
    if (this.getLength() === 0) {
      return null;
    }

    if (this.shouldRenderForSidebar()) {
      return this.renderForSidebar();
    }

    return (
      <OverflowingRow
        title={this.renderTitle()}
        titleHtml={this.props.titleHtml}
        subtitleHtml={this.props.subtitleHtml}
        ledeHtml={this.props.ledeHtml}
        action={this.renderAction()}
        appendAction={this.props.displayAsGrid}
        id={this.props.jumplinkSlug}
      >
        <Tiles
          grow={this.shouldGrow()}
          twoWide={this.shouldBeTwoWide()}
          threeWide={this.shouldBeThreeWide() || this.shouldBeSquare()}
          largeTiles={this.shouldBeLargeTiles()}
          fourWideMax={this.shouldBeFourWideMax()}
          singleRow={!this.props.displayAsGrid}
          expectedCount={this.getLength()}
          placeholder={<MarketingCalloutCard />}
          centered={this.shouldCenter()}
        >
          {this.renderCallouts()}
        </Tiles>
      </OverflowingRow>
    );
  }
}
